import { AuthResponse, LoginResponse, StoreMembership } from '../../types/company';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL as string;
const TOKEN_EXPIRATION_DAYS = 7;

// 店舗選択フロー中だけ使う一時情報の localStorage キー
const IDENTITY_TOKEN_KEY = 'identityToken';
const MEMBERSHIPS_KEY = 'memberships';

// ログイン日時（epoch ms）の localStorage キー。強制再ログイン判定に使う。
const LOGIN_AT_KEY = 'loginAt';

/**
 * 強制再ログイン適用日時（epoch ms）。
 * 認証関連 localStorage を破壊的に変更した際、この日時(JST)より前に確立したセッションへ
 * 再ログインを強制する。環境変数 REACT_APP_FORCED_RELOGIN_AT(YYYY-MM-DD HH:mm, JST)で環境ごとに指定。
 * 未設定・不正値なら本機能は無効（既存挙動のまま）。
 */
const FORCED_RELOGIN_AT = (() => {
  const raw = process.env.REACT_APP_FORCED_RELOGIN_AT;
  if (!raw || !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(raw)) return 0;
  const t = Date.parse(`${raw.replace(' ', 'T')}:00+09:00`); // 例 2026-06-29T14:00:00+09:00
  return Number.isNaN(t) ? 0 : t;
})();

/**
 * Google ID Token でログインする。
 * 本トークン（access/refresh）はまだ発行されず、所属一覧と一時トークンが返る。
 */
export async function googleLogin(idToken: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE_URL}/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });

  if (res.status === 403) {
    // 所属店舗が無い（未登録 or 削除済み）。呼び出し側で専用メッセージに振り分ける。
    throw new Error('NOT_REGISTERED');
  }
  if (res.status === 401) {
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    throw new Error('Login failed');
  }
  return (await res.json()) as LoginResponse;
}

/**
 * Apple ID Token でログインする（ADR 0036 / feast-be ADR 0074）。
 * フローは googleLogin と同じ（所属一覧 + 一時トークンが返る）。
 * Apple の ID トークンには氏名が含まれないため、初回認可時に取得できた場合のみ name を添える。
 */
export async function appleLogin(idToken: string, name?: string): Promise<LoginResponse> {
  const res = await fetch(`${API_BASE_URL}/auth/apple`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken, ...(name ? { name } : {}) }),
  });

  if (res.status === 403) {
    throw new Error('NOT_REGISTERED');
  }
  if (res.status === 401) {
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    throw new Error('Login failed');
  }
  return (await res.json()) as LoginResponse;
}

/**
 * 店舗を選択し、その店舗にスコープした本トークン（access/refresh）を発行する。
 * 一時トークン（identityToken）を Authorization ヘッダで送る。
 */
export async function selectStore(storeId: string): Promise<AuthResponse> {
  const identityToken = localStorage.getItem(IDENTITY_TOKEN_KEY);
  if (!identityToken) {
    throw new Error('No identity token. Please re-login.');
  }

  const res = await fetch(`${API_BASE_URL}/auth/select-store`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${identityToken}`,
    },
    body: JSON.stringify({ storeId }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    // 削除リクエスト中（ロック）の店舗は入室不可。専用マーカーで通知する。
    if (typeof data.error === 'string' && data.error.includes('削除リクエスト')) {
      throw new Error('STORE_LOCKED');
    }
    throw new Error('Failed to select store');
  }
  return (await res.json()) as AuthResponse;
}

/**
 * 所属する別の店舗へ切り替え、その店舗にスコープしたトークンを再発行する。
 * 現在のアクセストークンで本人確認する（ホーム画面の店舗切替）。
 */
export async function switchStore(storeId: string): Promise<AuthResponse> {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    throw new Error('No access token. Please re-login.');
  }
  const res = await fetch(`${API_BASE_URL}/auth/switch-store`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ storeId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    if (typeof data.error === 'string' && data.error.includes('削除リクエスト')) {
      throw new Error('STORE_LOCKED');
    }
    throw new Error('Failed to switch store');
  }
  return (await res.json()) as AuthResponse;
}

/**
 * 店舗のロック（削除リクエスト）を解除する。ロック中はスコープトークンを取得できないため、
 * 店舗選択と同じ一時トークン（identityToken）で本人確認する。
 */
export async function unlockStore(storeId: string): Promise<void> {
  const identityToken = localStorage.getItem(IDENTITY_TOKEN_KEY);
  if (!identityToken) {
    throw new Error('No identity token. Please re-login.');
  }
  const res = await fetch(`${API_BASE_URL}/auth/unlock-store`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${identityToken}`,
    },
    body: JSON.stringify({ storeId }),
  });
  if (!res.ok) {
    throw new Error('Failed to unlock store');
  }
}

/** ログイン応答（一時トークン・所属一覧）を保存する */
export function persistLogin(login: LoginResponse): void {
  localStorage.setItem(IDENTITY_TOKEN_KEY, login.identityToken);
  localStorage.setItem(MEMBERSHIPS_KEY, JSON.stringify(login.memberships));
  localStorage.setItem('email', login.email);
  localStorage.setItem('name', login.name);
  localStorage.setItem('picture', login.picture);
}

/** 店舗選択後の本セッション情報を保存し、一時情報を破棄する */
export function persistSession(auth: AuthResponse): void {
  const expiration = new Date().getTime() + TOKEN_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem('accessToken', auth.accessToken);
  localStorage.setItem('refreshToken', auth.refreshToken);
  localStorage.setItem('tokenExpiration', expiration.toString());
  localStorage.setItem('email', auth.email);
  localStorage.setItem('name', auth.name);
  localStorage.setItem('picture', auth.picture);
  localStorage.setItem('clerk_id', auth.clerkId);
  localStorage.setItem('store_id', auth.storeId);
  localStorage.setItem('role', auth.role);

  // 店舗選択フローの一時トークンは不要になるため破棄する。
  // 所属一覧（memberships）はホーム画面の店舗切替ドロップダウンで使うため残す（ログアウト時に clearSession で破棄）。
  localStorage.removeItem(IDENTITY_TOKEN_KEY);

  // 強制再ログイン判定用にログイン日時を記録する（全ログイン確定経路がここを通る）。
  localStorage.setItem(LOGIN_AT_KEY, Date.now().toString());
}

/**
 * 認証 localStorage の破壊的変更により再ログインが必要かを判定する。
 * loginAt が無い（旧セッション）、または FORCED_RELOGIN_AT より前のログインなら true。
 */
export function isReloginRequired(): boolean {
  if (!FORCED_RELOGIN_AT) return false; // 環境変数 未設定 = 機能無効
  if (!localStorage.getItem('accessToken')) return false; // 未ログインは対象外
  const loginAt = localStorage.getItem(LOGIN_AT_KEY);
  if (!loginAt) return true; // 記録なし = 旧セッション
  const t = parseInt(loginAt, 10);
  if (Number.isNaN(t)) return true;
  return t < FORCED_RELOGIN_AT;
}

/** 保存済みのログイン情報をすべて破棄する（ログアウト相当） */
export function clearSession(): void {
  localStorage.clear();
}

/**
 * 認証エラー時のフォールバック。
 * セッション情報を破棄し、ログイン画面へ遷移する（無限ループ防止のため /login 上では何もしない）。
 */
export function handleAuthFailure(): void {
  clearSession();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

/** 所属一覧を保存し直す（ロック解除などの状態変更を localStorage に反映する） */
export function persistMemberships(memberships: StoreMembership[]): void {
  localStorage.setItem(MEMBERSHIPS_KEY, JSON.stringify(memberships));
}

/** 保存済みの所属一覧を取得する（店舗選択ページで使用） */
export function getStoredMemberships(): StoreMembership[] {
  const raw = localStorage.getItem(MEMBERSHIPS_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as StoreMembership[];
  } catch {
    return [];
  }
}
